import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useTranslation as useT } from 'react-i18next';
import { Plus, MoreHorizontal, ExternalLink, RefreshCw, Search } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/common/DataTable';
import { api } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { useAuthStore } from '@/stores/auth';
import { siteSchema, type SiteDto } from '@/schemas';

void useAuthStore;

const sitesSearchSchema = z.object({
  q: z.string().optional(),
  status: z.enum(['all', 'active', 'pending', 'suspended']).optional(),
  page: z.number().int().nonnegative().optional(),
  new: z.number().optional(),
});

export const Route = createFileRoute('/_authed/sites/')({
  validateSearch: (input): { q?: string; status?: 'all' | 'active' | 'pending' | 'suspended'; page?: number; new?: number } => {
    const r = sitesSearchSchema.safeParse(input);
    return r.success ? r.data : {};
  },
  component: SitesListPage,
});

interface PaginatedSites {
  items: SiteDto[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

const mockSites: SiteDto[] = Array.from({ length: 18 }, (_, i) => ({
  id: `site_${i}`,
  ownerId: `user_${i}`,
  name: [
    'Aventura Pirineos',
    'Restaurante El Patio',
    'Estudio Norte',
    'Bodegas La Vid',
    'Clínica Dental Smile',
  ][i % 5],
  subdomain: `cliente${i}.cms.express`,
  plan: ['FREE', 'SILVER', 'GOLD', 'PLATINUM'][i % 4] as SiteDto['plan'],
  status: ['active', 'pending', 'suspended'][i % 3] as SiteDto['status'],
  createdAt: Date.now() - i * 86400_000 * 3,
  language: 'es',
}));

function SitesListPage() {
  const { t } = useTranslation();
  const { t: tc } = useT('common');
  const navigate = useNavigate();
  const search = Route.useSearch();
  useAuthStore();

  const { data, isLoading } = useQuery({
    queryKey: ['sites', search],
    queryFn: async (): Promise<PaginatedSites> => {
      try {
        return await api.get<PaginatedSites>('/admin/sites', {
          query: {
            q: search.q,
            status: search.status,
            page: search.page ?? 0,
          },
        });
      } catch {
        return {
          items: mockSites,
          pagination: { page: 0, limit: 20, total: mockSites.length, totalPages: 1 },
        };
      }
    },
  });

  React.useEffect(() => {
    if (search.new) {
      navigate({ to: '/sites', search: { new: undefined } });
    }
  }, [search.new, navigate]);

  const columns: ColumnDef<SiteDto>[] = React.useMemo(
    () => [
      {
        accessorKey: 'name',
        header: () => <span>{tc('name')}</span>,
        cell: ({ row }) => (
          <Link
            to="/sites/$siteId"
            params={{ siteId: row.original.id }}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'subdomain',
        header: 'Subdominio',
        cell: ({ row }) => (
          <a
            href={`https://${row.original.subdomain}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            {row.original.subdomain}
            <ExternalLink className="h-3 w-3" />
          </a>
        ),
      },
      {
        accessorKey: 'plan',
        header: 'Plan',
        cell: ({ row }) => (
          <Badge
            variant={
              row.original.plan === 'PLATINUM'
                ? 'default'
                : row.original.plan === 'GOLD'
                  ? 'warning'
                  : 'secondary'
            }
          >
            {row.original.plan}
          </Badge>
        ),
      },
      {
        accessorKey: 'status',
        header: () => <span>{tc('status')}</span>,
        cell: ({ row }) => (
          <Badge
            variant={
              row.original.status === 'active'
                ? 'success'
                : row.original.status === 'pending'
                  ? 'warning'
                  : 'destructive'
            }
          >
            {row.original.status}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: () => <span>{tc('created')}</span>,
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatRelative(row.original.createdAt)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">{tc('actions')}</span>,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={tc('actions')}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to="/sites/$siteId" params={{ siteId: row.original.id }}>
                  Ver detalle
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem>Suspender</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive">
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [tc],
  );

  void siteSchema;
  void t;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sitios</h1>
          <p className="text-muted-foreground">
            {data?.pagination.total ?? mockSites.length} sitios en la plataforma
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <RefreshCw className="mr-2 h-4 w-4" />
            Actualizar
          </Button>
          <Button asChild>
            <Link to="/sites/$siteId" params={{ siteId: 'new' }}>
              <Plus className="mr-2 h-4 w-4" />
              {t('sites.create')}
            </Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Listado de sitios</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={data?.items ?? mockSites}
            searchKey="name"
            searchPlaceholder="Buscar por nombre…"
            isLoading={isLoading}
            onRowClick={(row) =>
              navigate({ to: '/sites/$siteId', params: { siteId: row.id } })
            }
            toolbar={
              <Button variant="outline" size="sm">
                <Search className="mr-2 h-4 w-4" />
                Filtros
              </Button>
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
